import AssignmentIcon from '@mui/icons-material/Assignment';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PaidIcon from '@mui/icons-material/Paid';
import VerifiedIcon from '@mui/icons-material/Verified';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { BikeleasingIcon } from 'probonio-shared-ui/component/icon';
import { CircularProgressBar } from 'probonio-shared-ui/component/progress';
import { useStaticLinks } from 'probonio-shared-ui/module/links/hooks';
import { useTenant } from 'probonio-shared-ui/module/me';
import React, { ReactNode, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNewDialogState } from '../../../component/dialog';
import { FullWidthCenterBox } from '../../../component/layout/BoxLayout';
import { fontSizes, fontWeights } from '../../../theme';
import { BikeLeasingCustomerDialog } from './BikeleasingCustomerDialog';
import { useBikeleasingCondition } from './useBikeleasingCondition';

interface BLSListItemProps {
  title: string;
  description: string;
  icon: ReactNode;
}

export interface BenefitPrecondition {
  isPreconditionMet: boolean;
  PreconditionComponent: React.ReactNode;
}

const BLSListItem = ({ title, description, icon }: BLSListItemProps) => {
  const theme = useTheme();
  return (
    <ListItem disablePadding sx={{ marginY: 2 }}>
      <ListItemAvatar>
        <Avatar sx={{ color: theme.palette.primary.main, backgroundColor: theme.palette.grey.A200, height: 50, width: 50, mr: 2 }}>
          {icon}
        </Avatar>
      </ListItemAvatar>
      <ListItemText>
        <Typography variant="subtitle1" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {description}
        </Typography>
      </ListItemText>
    </ListItem>
  );
};

const PreconditionComponent: React.FC<{ isPreconditionMet: boolean; inBikeleasingOnboarding?: boolean; onSuccess?: () => void }> = ({
  isPreconditionMet,
  inBikeleasingOnboarding,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('benefitsModule');
  const links = useStaticLinks();
  const { invalidateTenant } = useTenant();

  const bikeleasingDialog = useNewDialogState();

  if (isPreconditionMet) return null;

  return (
    <>
      {links?.BLS_WEB && (
        <Box width="100%" display="flex" justifyContent="center" my={5}>
          <IconButton href={links?.BLS_WEB} target="_blank" focusRipple={false} disableTouchRipple disableRipple sx={{ width: '100%' }}>
            <BikeleasingIcon width="60%" />
          </IconButton>
        </Box>
      )}
      <Box mx={4}>
        <Typography fontSize={fontSizes.lg}>{t('extensions:bikeleasing_v2_benefit.noAccount.info1')}</Typography>
        <Typography variant="h2" mt={3} gutterBottom>
          {t('extensions:bikeleasing_v2_benefit.advantages.title')}
        </Typography>
        <List disablePadding>
          <BLSListItem
            title={t('extensions:bikeleasing_v2_benefit.advantages.lessCosts.title')}
            description={t('extensions:bikeleasing_v2_benefit.advantages.lessCosts.description')}
            icon={<PaidIcon />}
          />
          <BLSListItem
            title={t('extensions:bikeleasing_v2_benefit.advantages.climateFriendly.title')}
            description={t('extensions:bikeleasing_v2_benefit.advantages.climateFriendly.description')}
            icon={<VerifiedUserIcon />}
          />
          <BLSListItem
            title={t('extensions:bikeleasing_v2_benefit.advantages.security.title')}
            description={t('extensions:bikeleasing_v2_benefit.advantages.security.description')}
            icon={<AssignmentIcon />}
          />
        </List>
        <Box color={theme.palette.grey.A700} sx={{ mt: 3 }}>
          <Typography>{t('extensions:bikeleasing_v2.pendingOnboarding')}</Typography>
          <Typography variant="body2">
            <Trans i18nKey="extensions:bikeleasing_v2.pendingOnboardingCaption">
              <Link href={links?.BLS_PRIVACY} target="_blank" rel="noopener noreferrer" color="inherit" />
            </Trans>
          </Typography>
        </Box>
        {links?.BLS_AG && !inBikeleasingOnboarding && (
          <Box display="flex" justifyContent="center" my={3}>
            <Button startIcon={<OpenInNewIcon />} onClick={bikeleasingDialog.handleOpen}>
              {t('extensions:bikeleasing_v2_benefit.integration.bikeleasingPortal')}
            </Button>
          </Box>
        )}
        {inBikeleasingOnboarding && (
          <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center', my: 4 }}>
            <VerifiedIcon color="success" sx={{ fontSize: 60, marginBottom: 2, opacity: 0.8 }} />
            <Typography variant="h2">{t('extensions:bikeleasing_v2.integration.success.title')}</Typography>
            <Typography variant="body1">{t('extensions:bikeleasing_v2.integration.success.description')}</Typography>
          </Stack>
        )}
        <Divider sx={{ mb: 3 }} />
        <Typography variant="h2" fontWeight={fontWeights.semiBold} gutterBottom>
          {t('extensions:bikeleasing_v2_benefit.questions.title')}
        </Typography>
        {links?.BLS_WEB && (
          <Typography gutterBottom color="text.secondary">
            <Trans
              i18nKey="extensions:bikeleasing_v2_benefit.questions.description"
              values={{ web: links.BLS_WEB, tel: links.BLS_TEL, mail: links.MAIL }}
            >
              <a href={links.BLS_WEB} target="_blank" rel="noreferrer" />
            </Trans>
          </Typography>
        )}
      </Box>
      <BikeLeasingCustomerDialog dialogState={bikeleasingDialog.dialogState} onSuccess={invalidateTenant} />
    </>
  );
};
export function useBikeleasingPrecondition(onSuccess?: () => void): BenefitPrecondition {
  const { data: bikeleasingCondition, isLoading, isFetching } = useBikeleasingCondition();
  const isPreconditionMet = !!bikeleasingCondition;
  const { tenant } = useTenant();
  const inBikeleasingOnboarding = tenant?.inBikeleasingOnboarding;

  const component = useMemo(
    () =>
      isFetching || isLoading ? (
        <FullWidthCenterBox>
          <CircularProgressBar />
        </FullWidthCenterBox>
      ) : (
        <PreconditionComponent
          isPreconditionMet={isPreconditionMet}
          inBikeleasingOnboarding={inBikeleasingOnboarding}
          onSuccess={onSuccess}
        />
      ),
    [isFetching, isLoading, isPreconditionMet, inBikeleasingOnboarding, onSuccess],
  );

  return useMemo(
    () => ({ isPreconditionMet, inBikeleasingOnboarding, PreconditionComponent: component }),
    [component, inBikeleasingOnboarding, isPreconditionMet],
  );
}
